/**
 * Production Campaign — multi-machine, multi-day, sales-order-linked.
 * ─────────────────────────────────────────────────────────────────────────────
 * Replaces the single-machine-per-row Production Plan with the management
 * unit: "Run RF-01..RF-03 on 30s carded knitting for 5 days against
 * SO-2026-0145".
 *
 * Two creation paths:
 *   1. From Sales Order — pulls count/yarn/qty/notes from a confirmed SO
 *   2. Ad-hoc           — explicit count/yarn/qty (rare, for edge cases)
 *
 * Lifecycle: PLANNED → RUNNING → COMPLETED, with CANCELLED escape.
 *
 * Daily target is auto-computed from the productivity master (114-row spec)
 * × allocated machines × shifts × hours per shift. The preview tab shows
 * the math live as the user picks machines.
 */
import { useMemo, useState } from 'react'
import {
  Alert, App, Button, Card, Col, DatePicker, Divider, Drawer, Empty,
  Form, Input, InputNumber, Popconfirm, Row, Select, Space, Spin,
  Statistic, Table, Tag, Tooltip, Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  ApartmentOutlined,
  CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined,
  DeleteOutlined,
  EyeOutlined, PlayCircleOutlined, PlusOutlined,
  RocketOutlined, StopOutlined, SyncOutlined, ThunderboltOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'
import PageHeader from '@/shared/components/PageHeader'

const { Text } = Typography

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface CampaignMachine {
  id: string
  machine_id: string
  spindle_count: number
  override_rpm: number | null
  override_tm: string | null
  override_oee_decimal: string | null
  computed_gps: string | null
  computed_daily_kg: string | null
  actual_kg: string
  notes: string | null
}

interface Campaign {
  id: string
  campaign_no: string
  sales_order_id: string | null
  count_ne: string
  yarn_type: string
  end_use: string | null
  count_description: string | null
  start_date: string
  planned_end_date: string | null
  actual_end_date: string | null
  required_days: string | null
  shifts_per_day: number
  hours_per_shift: number
  target_quantity_kg: string
  target_daily_kg: string | null
  actual_quantity_kg: string
  status: string
  approved_at: string | null
  started_at: string | null
  completed_at: string | null
  notes_to_production: string | null
  notes: string | null
  machines: CampaignMachine[]
}

interface PreviewResult {
  count_ne: string
  yarn_type: string
  target_quantity_kg: string
  target_daily_kg: string | null
  required_days: string | null
  productivity_basis: string | null
  warnings: string[]
  machine_lines: Array<{
    machine_id: string
    machine_code: string | null
    spindle_count: number
    rpm: number | null
    tm: string | null
    oee_decimal: string | null
    gps: string | null
    daily_kg: string | null
    inputs_source: string
  }>
}

interface SalesOrder {
  id: string
  so_no: string
  count_ne: string | null
  yarn_type: string | null
  end_use: string | null
  quantity_kg: string
  customer_code: string | null
  customer_name: string | null
  status: string
  delivery_date: string
  production_type: string
}

interface MachineRow {
  id: string
  machine_code: string
  machine_name: string
  machine_type: string
  spindle_count: number | null
  machine_status: string
  is_active: boolean
}

const YARN_TYPES = ['CARDED', 'CARDED_COMPACT', 'COMPACT', 'SIRO', 'COMBED', 'COMBED_COMPACT']
const COUNTS = [8, 10, 12, 16, 20, 24, 30, 32, 34, 40, 45, 50, 60, 70, 80, 90, 100, 110, 120]

const STATUS_TAG: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
  PLANNED:   { color: 'blue',    icon: <ClockCircleOutlined />, label: 'Planned' },
  RUNNING:   { color: 'orange',  icon: <SyncOutlined spin />,   label: 'Running' },
  COMPLETED: { color: 'green',   icon: <CheckCircleOutlined />, label: 'Completed' },
  CANCELLED: { color: 'red',     icon: <CloseCircleOutlined />, label: 'Cancelled' },
}

export default function ProductionCampaignPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plantId } = usePlantId()

  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [activeCampaign, setActiveCampaign] = useState<Campaign | null>(null)
  // PROD-001A.1 — drawer for adding/removing machines on an existing campaign
  const [manageMachinesFor, setManageMachinesFor] = useState<Campaign | null>(null)
  const [createMode, setCreateMode] = useState<'so' | 'adhoc'>('so')
  const [form] = Form.useForm()

  // ─── Data fetch ──────────────────────────────────────────────────────
  const { data: campaigns = [], isLoading } = useQuery<Campaign[]>({
    queryKey: ['production-campaigns', plantId, statusFilter],
    queryFn: () =>
      apiClient.get('/production-campaigns', {
        params: { plant_id: plantId, status: statusFilter },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  // Machine master lives at /masters/machines (plural). The endpoint
  // returns a PaginatedResponse {items, total, page, size, pages} — not
  // a bare array — so we dig into .items. Also bumped page size so all
  // ring frames in a typical mill (≤100) show up without paging.
  //
  // Response field names — the backend MachineResponse exposes renamed
  // properties:
  //   machine_id_str  → maps to machine_code
  //   name            → maps to machine_name
  //   section         → maps to machine_type
  // So we normalise here. Without this the dropdown rendered
  // "undefined · — · — · ..." since the raw fields the frontend
  // assumed (machine_code/machine_type) don't exist on the wire.
  const {
    data: machines = [],
    isLoading: machinesLoading,
    error: machinesError,
  } = useQuery<MachineRow[]>({
    queryKey: ['machines-active', plantId],
    queryFn: async () => {
      const r = await apiClient.get('/masters/machines', {
        params: { plant_id: plantId, is_active: true, size: 100 },
      })
      const raw: any[] = Array.isArray(r.data) ? r.data : (r.data?.items ?? [])
      return raw.map((m): MachineRow => ({
        id: m.id,
        machine_code: m.machine_code ?? m.machine_id_str ?? '',
        machine_name: m.machine_name ?? m.name ?? '',
        machine_type: m.machine_type ?? m.section ?? '',
        spindle_count: m.spindle_count ?? null,
        machine_status: m.machine_status ?? '',
        is_active: m.is_active ?? true,
      }))
    },
    // Fetch when EITHER drawer (Create/View) is open so the View
    // drawer can resolve UUID → machine_name without a separate query.
    enabled: !!plantId && (drawerOpen || !!activeCampaign),
  })

  // machine_id → MachineRow lookup for the View drawer's machines
  // table. Without this we'd have to bake names into the campaign
  // response (requires a relationship + schema change). Cheap to
  // compute and stays fresh as the master is edited.
  const machineById = useMemo(() => {
    const m: Record<string, MachineRow> = {}
    machines.forEach(x => { m[x.id] = x })
    return m
  }, [machines])

  // Ring-frame matcher — case/separator-insensitive. Master entries
  // can come in as "RING_FRAME", "Ring Frame", "ring-frame", etc.;
  // we treat them all as ring frames so the dropdown isn't empty
  // just because the data was typed differently.
  const isRingFrame = (m: MachineRow) =>
    /ring.?frame/i.test(m.machine_type ?? '')

  // Build dropdown options. If we have any ring frames, show only
  // those (most common case for production planning). Otherwise fall
  // back to ALL active machines so the user can still proceed and
  // we give them a hint via notFoundContent.
  const ringFrameMachines = machines.filter(isRingFrame)
  // Label primarily by machine_name (e.g. "RF-1") because that's what
  // the floor staff actually call the machine. Fall back to machine_code
  // when name is blank. Spindle count + status follow as quick context.
  const machineOptions = (ringFrameMachines.length > 0
    ? ringFrameMachines
    : machines
  ).map(m => {
    const display = m.machine_name?.trim() || m.machine_code || '—'
    return {
      value: m.id,
      label: `${display} · ${m.spindle_count ?? '—'} sp · ${m.machine_status ?? '—'}`,
    }
  })

  // For create-from-SO
  const { data: salesOrders = [] } = useQuery<SalesOrder[]>({
    queryKey: ['sales-orders-confirmed', plantId],
    queryFn: () =>
      apiClient.get('/sales-orders/production-view', {
        params: { plant_id: plantId, status: 'CONFIRMED' },
      }).then(r => r.data),
    enabled: !!plantId && drawerOpen && createMode === 'so',
  })

  // ─── Preview (live as user picks) ────────────────────────────────────
  const [previewState, setPreviewState] = useState<PreviewResult | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  const runPreview = async () => {
    if (!plantId) return
    const vals = form.getFieldsValue()
    if (!vals.machines?.length || !vals.count_ne || !vals.yarn_type
        || !vals.target_quantity_kg) return
    setPreviewLoading(true)
    try {
      const r = await apiClient.post('/production-campaigns/preview', {
        plant_id: plantId,
        count_ne: vals.count_ne,
        yarn_type: vals.yarn_type,
        target_quantity_kg: vals.target_quantity_kg,
        machines: (vals.machines ?? []).map((m: any) => ({
          machine_id: m.machine_id,
          override_rpm: m.override_rpm || null,
          override_tm: m.override_tm || null,
          override_oee_decimal: m.override_oee_decimal || null,
        })),
        shifts_per_day: vals.shifts_per_day ?? 3,
        hours_per_shift: vals.hours_per_shift ?? 8,
      })
      setPreviewState(r.data)
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Preview failed')
    } finally {
      setPreviewLoading(false)
    }
  }

  // ─── Mutations ───────────────────────────────────────────────────────
  const createFromSOMut = useMutation({
    mutationFn: ({ soId, body }: { soId: string; body: any }) =>
      apiClient.post(`/production-campaigns/from-sales-order/${soId}`, body)
        .then(r => r.data),
    onSuccess: () => {
      message.success('Production plan created ✓')
      qc.invalidateQueries({ queryKey: ['production-campaigns', plantId] })
      setDrawerOpen(false); form.resetFields(); setPreviewState(null)
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Create failed'),
  })

  const createAdHocMut = useMutation({
    mutationFn: (body: any) =>
      apiClient.post('/production-campaigns', body).then(r => r.data),
    onSuccess: () => {
      message.success('Ad-hoc production plan created ✓')
      qc.invalidateQueries({ queryKey: ['production-campaigns', plantId] })
      setDrawerOpen(false); form.resetFields(); setPreviewState(null)
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Create failed'),
  })

  const lifecycleMut = useMutation({
    // BUG FIX — When body is omitted (e.g. cancel/start/approve calls),
    // we used to send `{}`. FastAPI interprets a single Body parameter
    // of type `str | None` as the body itself (not embedded), so `{}`
    // fails validation with a 422 + detail = array of error objects.
    // That array was passed straight to message.error(), which AntD
    // tried to render as React children — "Objects are not valid as a
    // React child" → app-wide white screen.
    //
    // Two-line fix:
    //   1. Send `null` instead of `{}` when there's nothing to pass —
    //      axios omits the JSON body, FastAPI uses the default None.
    //   2. Coerce e.response.data.detail to a string before toasting,
    //      so any future shape (string / array / object) is safe.
    mutationFn: ({ id, action, body }: { id: string; action: string; body?: any }) =>
      apiClient.post(`/production-campaigns/${id}/${action}`, body ?? null)
        .then(r => r.data),
    onSuccess: (_, vars) => {
      message.success(`✓ ${vars.action} done`)
      qc.invalidateQueries({ queryKey: ['production-campaigns', plantId] })
    },
    onError: (e: any) => {
      const detail = e?.response?.data?.detail
      let msg = 'Action failed'
      if (typeof detail === 'string') msg = detail
      else if (Array.isArray(detail)) {
        msg = detail.map((d: any) => d?.msg || d?.detail || JSON.stringify(d)).join('; ')
      } else if (detail && typeof detail === 'object') {
        msg = detail.message || detail.detail || JSON.stringify(detail)
      }
      message.error(msg)
    },
  })

  // ─── Submit ──────────────────────────────────────────────────────────
  const handleSubmit = () => {
    form.validateFields().then(vals => {
      const machinesPayload = (vals.machines ?? []).map((m: any) => ({
        machine_id: m.machine_id,
        override_rpm: m.override_rpm || null,
        override_tm: m.override_tm || null,
        override_oee_decimal: m.override_oee_decimal || null,
        notes: m.notes || null,
      }))

      if (createMode === 'so') {
        if (!vals.sales_order_id) {
          message.error('Pick a Sales Order')
          return
        }
        createFromSOMut.mutate({
          soId: vals.sales_order_id,
          body: {
            machines: machinesPayload,
            start_date: vals.start_date.format('YYYY-MM-DD'),
            shifts_per_day: vals.shifts_per_day ?? 3,
            hours_per_shift: vals.hours_per_shift ?? 8,
          },
        })
      } else {
        createAdHocMut.mutate({
          plant_id: plantId,
          start_date: vals.start_date.format('YYYY-MM-DD'),
          machines: machinesPayload,
          shifts_per_day: vals.shifts_per_day ?? 3,
          hours_per_shift: vals.hours_per_shift ?? 8,
          count_ne: vals.count_ne,
          yarn_type: vals.yarn_type,
          end_use: vals.end_use,
          target_quantity_kg: vals.target_quantity_kg,
          notes_to_production: vals.notes_to_production,
        })
      }
    })
  }

  // ─── KPIs ────────────────────────────────────────────────────────────
  const kpi = useMemo(() => {
    const planned = campaigns.filter(c => c.status === 'PLANNED').length
    const running = campaigns.filter(c => c.status === 'RUNNING').length
    const completed = campaigns.filter(c => c.status === 'COMPLETED').length
    const totalKgRunning = campaigns
      .filter(c => c.status === 'RUNNING')
      .reduce((s, c) => s + Number(c.target_quantity_kg || 0), 0)
    return { planned, running, completed, totalKgRunning }
  }, [campaigns])

  // ─── Columns ─────────────────────────────────────────────────────────
  const columns: ColumnsType<Campaign> = [
    { title: 'Plan No.', dataIndex: 'campaign_no', key: 'cn', width: 140,
      render: (v: string, r) => (
        <Space direction="vertical" size={0}>
          <Text code style={{ fontSize: 12 }}>{v}</Text>
          {r.sales_order_id && <Tag color="blue" style={{ fontSize: 9, margin: 0 }}>SO-linked</Tag>}
          {!r.sales_order_id && <Tag color="orange" style={{ fontSize: 9, margin: 0 }}>Ad-hoc</Tag>}
        </Space>
      ) },
    { title: 'What', key: 'what', width: 180,
      render: (_: unknown, r) => (
        <Space direction="vertical" size={0} style={{ fontSize: 11 }}>
          <span><b>{Number(r.count_ne || 0).toFixed(0)}s {(r.yarn_type || '').replace(/_/g, ' ')}</b></span>
          {r.end_use && <Text type="secondary">{r.end_use}</Text>}
          {r.count_description && <Text type="secondary" style={{ fontSize: 10 }}>{r.count_description}</Text>}
        </Space>
      ) },
    { title: 'Schedule', key: 'sched', width: 140,
      render: (_: unknown, r) => (
        <Space direction="vertical" size={0} style={{ fontSize: 11 }}>
          <span><b>{r.start_date}</b></span>
          {r.planned_end_date && <Text type="secondary">→ {r.planned_end_date}</Text>}
          {r.required_days && <Tag style={{ fontSize: 10 }}>{r.required_days} days</Tag>}
        </Space>
      ) },
    { title: 'Machines', key: 'machines', width: 90,
      render: (_: unknown, r) => {
        const n = (r.machines || []).length
        return (
          <Tooltip title={`${n} machine(s) × ${r.shifts_per_day} shifts/day`}>
            <Space direction="vertical" size={0} style={{ fontSize: 11 }}>
              <Text strong>{n}</Text>
              <Text type="secondary" style={{ fontSize: 10 }}>{r.shifts_per_day} sh × {r.hours_per_shift}h</Text>
            </Space>
          </Tooltip>
        )
      } },
    { title: 'Target', key: 'tgt', width: 130, align: 'right',
      render: (_: unknown, r) => (
        <Space direction="vertical" size={0} style={{ fontSize: 11 }}>
          <Text strong>{Number(r.target_quantity_kg).toFixed(0)} kg</Text>
          {r.target_daily_kg && (
            <Text type="secondary" style={{ fontSize: 10 }}>
              {Number(r.target_daily_kg).toFixed(0)} kg/day
            </Text>
          )}
        </Space>
      ) },
    { title: 'Actual', key: 'actual', width: 130, align: 'right',
      render: (_: unknown, r) => {
        const tgt = Number(r.target_quantity_kg) || 0
        const act = Number(r.actual_quantity_kg) || 0
        const pct = tgt > 0 ? (act / tgt) * 100 : 0
        return (
          <Space direction="vertical" size={0} style={{ fontSize: 11 }}>
            <Text strong>{act.toFixed(0)} kg</Text>
            <Text type="secondary" style={{ fontSize: 10 }}>{pct.toFixed(0)}%</Text>
          </Space>
        )
      } },
    { title: 'Status', dataIndex: 'status', key: 'st', width: 130,
      render: (v: string) => {
        const cfg = STATUS_TAG[v] ?? { color: 'default', label: v, icon: null }
        return <Tag icon={cfg.icon} color={cfg.color}>{cfg.label}</Tag>
      } },
    { title: 'Action', key: 'action', width: 280, fixed: 'right' as const,
      render: (_: unknown, r) => (
        <Space size={4} wrap>
          <Button size="small" icon={<EyeOutlined />}
            onClick={() => setActiveCampaign(r)}>View</Button>
          {(r.status === 'PLANNED' || r.status === 'RUNNING') && (
            <Button size="small" icon={<ApartmentOutlined />}
              onClick={() => setManageMachinesFor(r)}>
              Machines
            </Button>
          )}
          {r.status === 'PLANNED' && !r.approved_at && (
            <Button size="small" type="link" icon={<CheckCircleOutlined />}
              onClick={() => lifecycleMut.mutate({ id: r.id, action: 'approve' })}>
              Approve
            </Button>
          )}
          {r.status === 'PLANNED' && r.approved_at && (
            <Button size="small" type="primary" icon={<PlayCircleOutlined />}
              onClick={() => lifecycleMut.mutate({ id: r.id, action: 'start' })}>
              Start
            </Button>
          )}
          {r.status === 'RUNNING' && (
            <Popconfirm title="Mark this production plan complete?"
              onConfirm={() => lifecycleMut.mutate({ id: r.id, action: 'complete' })}>
              <Button size="small" type="link" icon={<CheckCircleOutlined />}>Complete</Button>
            </Popconfirm>
          )}
          {(r.status === 'PLANNED' || r.status === 'RUNNING') && (
            <Popconfirm title="Cancel this production plan?"
              onConfirm={() => lifecycleMut.mutate({ id: r.id, action: 'cancel' })}>
              <Button size="small" type="link" danger icon={<StopOutlined />}>Cancel</Button>
            </Popconfirm>
          )}
        </Space>
      ) },
  ]

  return (
    <div>
      <PageHeader
        title="Production Plan"
        subtitle="Multi-machine, multi-day production runs — auto-target from productivity master"
        extra={
          <Space>
            <Select allowClear placeholder="Filter status"
              style={{ width: 160 }} value={statusFilter} onChange={setStatusFilter}
              options={Object.keys(STATUS_TAG).map(k => ({
                value: k, label: STATUS_TAG[k].label,
              }))} />
            <Button type="primary" icon={<PlusOutlined />}
              onClick={() => { setCreateMode('so'); setDrawerOpen(true) }}>
              New Production Plan
            </Button>
          </Space>
        }
      />

      {/* KPIs */}
      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col xs={6}>
          <Card size="small" style={{ borderTop: '3px solid #1677ff' }}>
            <Statistic title="Planned" value={kpi.planned}
              valueStyle={{ fontSize: 22, color: '#1677ff', fontWeight: 700 }}
              prefix={<ClockCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={6}>
          <Card size="small" style={{ borderTop: '3px solid #faad14' }}>
            <Statistic title="Running" value={kpi.running}
              valueStyle={{ fontSize: 22, color: '#faad14', fontWeight: 700 }}
              prefix={<SyncOutlined spin />} />
          </Card>
        </Col>
        <Col xs={6}>
          <Card size="small" style={{ borderTop: '3px solid #52c41a' }}>
            <Statistic title="Completed" value={kpi.completed}
              valueStyle={{ fontSize: 22, color: '#52c41a', fontWeight: 700 }}
              prefix={<CheckCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={6}>
          <Card size="small" style={{ borderTop: '3px solid #722ed1' }}>
            <Statistic title="Running Volume"
              value={kpi.totalKgRunning.toFixed(0)} suffix="kg"
              valueStyle={{ fontSize: 22, color: '#722ed1', fontWeight: 700 }}
              prefix={<RocketOutlined />} />
          </Card>
        </Col>
      </Row>

      {/* Main table */}
      <Card size="small">
        {!plantId ? <Empty description="Select a plant first" />
          : <Table rowKey="id" size="small" loading={isLoading}
              dataSource={campaigns} columns={columns}
              scroll={{ x: 1100 }}
              pagination={{ pageSize: 25, showTotal: t => `${t} plans` }} />}
      </Card>

      {/* Create drawer */}
      <Drawer
        title={
          <Space>
            <RocketOutlined style={{ color: '#1677ff' }} />
            New Production Plan
          </Space>
        }
        open={drawerOpen}
        width={720}
        onClose={() => { setDrawerOpen(false); setPreviewState(null) }}
        footer={
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Button onClick={runPreview} icon={<ThunderboltOutlined />}
              loading={previewLoading}>
              Preview Targets
            </Button>
            <Space>
              <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
              <Button type="primary" onClick={handleSubmit}
                loading={createFromSOMut.isPending || createAdHocMut.isPending}>
                Create Plan
              </Button>
            </Space>
          </Space>
        }
      >
        {/* Mode toggle */}
        <Card size="small" style={{ marginBottom: 12 }}>
          <Space>
            <Text>Source:</Text>
            <Button.Group>
              <Button type={createMode === 'so' ? 'primary' : 'default'}
                onClick={() => setCreateMode('so')}>From Sales Order</Button>
              <Button type={createMode === 'adhoc' ? 'primary' : 'default'}
                onClick={() => setCreateMode('adhoc')}>Ad-hoc</Button>
            </Button.Group>
          </Space>
        </Card>

        <Form form={form} layout="vertical"
          initialValues={{ shifts_per_day: 3, hours_per_shift: 8,
                            start_date: dayjs().add(1, 'day') }}>
          {createMode === 'so' && (
            <Form.Item name="sales_order_id" label="Sales Order"
              rules={[{ required: true, message: 'Pick an SO' }]}>
              <Select
                showSearch optionFilterProp="label"
                placeholder="Pick a confirmed SO"
                options={salesOrders.map(s => ({
                  value: s.id,
                  label: `${s.so_no} · ${Number(s.count_ne ?? 0).toFixed(0)}s ${s.yarn_type ?? '?'} · ${Number(s.quantity_kg).toFixed(0)} kg · ${s.customer_code ?? '—'}`,
                }))}
              />
            </Form.Item>
          )}

          {createMode === 'adhoc' && (
            <>
              <Alert type="info" showIcon style={{ marginBottom: 12 }}
                message="Ad-hoc plans are for edge cases. Most production should flow through Sales Orders or Stock-Build Authorisations." />
              <Row gutter={12}>
                <Col span={8}>
                  <Form.Item name="count_ne" label="Count (Ne)" rules={[{ required: true }]}>
                    <Select options={COUNTS.map(c => ({ value: c, label: `${c}s` }))} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="yarn_type" label="Yarn Type" rules={[{ required: true }]}>
                    <Select options={YARN_TYPES.map(t => ({ value: t, label: t.replace(/_/g, ' ') }))} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="end_use" label="End Use">
                    <Select options={[
                      { value: 'KNITTING', label: 'Knitting' },
                      { value: 'WEAVING', label: 'Weaving' },
                      { value: 'DYEING', label: 'Dyeing' },
                      { value: 'INDUSTRIAL', label: 'Industrial' },
                    ]} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="target_quantity_kg" label="Target Quantity (kg)"
                    rules={[{ required: true }]}>
                    <InputNumber style={{ width: '100%' }} min={0} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="notes_to_production" label="Notes to Production">
                    <Input placeholder="Floor-safe notes" />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          <Divider orientation="left" style={{ fontSize: 12 }}>Schedule</Divider>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="start_date" label="Start Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="shifts_per_day" label="Shifts / Day">
                <InputNumber style={{ width: '100%' }} min={1} max={4} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="hours_per_shift" label="Hours / Shift">
                <InputNumber style={{ width: '100%' }} min={1} max={12} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ fontSize: 12 }}>
            <Space size={8}>
              <span>Machine Allocation</span>
              {machinesLoading && <Tag color="blue">Loading…</Tag>}
              {!!machinesError && <Tag color="red">Fetch failed</Tag>}
              {!machinesLoading && !machinesError && (
                <Tag color={machineOptions.length ? 'green' : 'orange'}>
                  {machineOptions.length} machine{machineOptions.length === 1 ? '' : 's'} available
                  {ringFrameMachines.length === 0 && machines.length > 0
                    ? ' (no RING_FRAME — showing all)'
                    : ''}
                </Tag>
              )}
            </Space>
          </Divider>
          <Form.List name="machines"
            rules={[{
              validator: async (_, list) => {
                if (!list?.length) throw new Error('Allocate at least one machine')
              },
            }]}>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name }) => (
                  <Row gutter={6} key={key} style={{ marginBottom: 6 }}>
                    <Col span={9}>
                      <Form.Item name={[name, 'machine_id']} rules={[{ required: true }]}
                        style={{ marginBottom: 0 }}>
                        <Select
                          showSearch
                          optionFilterProp="label"
                          placeholder={
                            machinesLoading
                              ? 'Loading machines…'
                              : machinesError
                                ? 'Failed to load machines'
                                : machineOptions.length === 0
                                  ? 'No machines — add one in Masters'
                                  : 'Pick a machine'
                          }
                          loading={machinesLoading}
                          options={machineOptions}
                          notFoundContent={
                            machinesLoading
                              ? 'Loading…'
                              : machines.length === 0
                                ? 'No active machines for this plant. Add machines under Masters → Machines.'
                                : 'No machines match. Check machine_type / is_active in Masters → Machines.'
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item name={[name, 'override_rpm']} style={{ marginBottom: 0 }}>
                        <InputNumber style={{ width: '100%' }} placeholder="RPM" min={0} />
                      </Form.Item>
                    </Col>
                    <Col span={3}>
                      <Form.Item name={[name, 'override_tm']} style={{ marginBottom: 0 }}>
                        <InputNumber style={{ width: '100%' }} placeholder="TM" min={0} step={0.1} />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item name={[name, 'override_oee_decimal']} style={{ marginBottom: 0 }}>
                        <InputNumber style={{ width: '100%' }} placeholder="OEE" min={0} max={1} step={0.01} />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Button danger size="small" onClick={() => remove(name)}>Remove</Button>
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add({})} block icon={<PlusOutlined />}>
                  Add Machine
                </Button>
              </>
            )}
          </Form.List>

          {/* Live preview block */}
          {previewState && (
            <>
              <Divider orientation="left" style={{ fontSize: 12 }}>
                <ThunderboltOutlined /> Predicted Output
              </Divider>
              {previewState.warnings?.length > 0 && (
                <Alert type="warning" showIcon style={{ marginBottom: 8 }}
                  message={
                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                      {previewState.warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  } />
              )}
              <Row gutter={12}>
                <Col span={8}>
                  <Statistic title="Daily kg target"
                    value={previewState.target_daily_kg ?? '—'} suffix="kg"
                    precision={0} valueStyle={{ fontSize: 20 }} />
                </Col>
                <Col span={8}>
                  <Statistic title="Days needed"
                    value={previewState.required_days ?? '—'} suffix="days"
                    valueStyle={{ fontSize: 20 }} />
                </Col>
                <Col span={8}>
                  <Statistic title="Productivity basis"
                    valueRender={() => <Tag>{previewState.productivity_basis ?? '—'}</Tag>} />
                </Col>
              </Row>
              <Table size="small" rowKey="machine_id" pagination={false}
                style={{ marginTop: 8 }}
                dataSource={previewState.machine_lines}
                columns={[
                  { title: 'Machine', dataIndex: 'machine_id', width: 140,
                    render: (mid: string, row: any) => {
                      const m = machineById[mid]
                      const name = m?.machine_name?.trim() || row.machine_code || m?.machine_code || '—'
                      return <Text strong style={{ fontSize: 12 }}>{name}</Text>
                    } },
                  { title: 'Spindles', dataIndex: 'spindle_count', width: 80, align: 'right' },
                  { title: 'RPM', dataIndex: 'rpm', width: 70, align: 'right' },
                  { title: 'TM', dataIndex: 'tm', width: 60, align: 'right' },
                  { title: 'OEE', dataIndex: 'oee_decimal', width: 70, align: 'right',
                    render: (v: string | null) => v ? `${(Number(v) * 100).toFixed(0)}%` : '—' },
                  { title: 'GPS', dataIndex: 'gps', width: 70, align: 'right',
                    render: (v: string | null) => v ? Number(v).toFixed(1) : '—' },
                  { title: 'kg/day', dataIndex: 'daily_kg', width: 90, align: 'right',
                    render: (v: string | null) => v ? <b>{Number(v).toFixed(0)}</b> : '—' },
                  { title: 'Source', dataIndex: 'inputs_source', width: 90,
                    render: (v: string) => <Tag style={{ fontSize: 9 }}>{v.replace(/_/g, ' ')}</Tag> },
                ]}
              />
            </>
          )}
        </Form>
      </Drawer>

      {/* Detail drawer */}
      <Drawer
        title={activeCampaign && (
          <Space wrap>
            <Text code>{activeCampaign.campaign_no}</Text>
            <Tag color={STATUS_TAG[activeCampaign.status]?.color}>
              {STATUS_TAG[activeCampaign.status]?.label}
            </Tag>
          </Space>
        )}
        open={!!activeCampaign}
        width={700}
        onClose={() => setActiveCampaign(null)}
      >
        {activeCampaign && (
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            <Card size="small">
              <Row gutter={8}>
                <Col span={8}>
                  <Statistic title="Target" value={Number(activeCampaign.target_quantity_kg).toFixed(0)} suffix="kg"
                    valueStyle={{ fontSize: 18 }} />
                </Col>
                <Col span={8}>
                  <Statistic title="Actual" value={Number(activeCampaign.actual_quantity_kg).toFixed(0)} suffix="kg"
                    valueStyle={{ fontSize: 18, color: '#1677ff' }} />
                </Col>
                <Col span={8}>
                  <Statistic title="Daily Target" value={activeCampaign.target_daily_kg ? Number(activeCampaign.target_daily_kg).toFixed(0) : '—'} suffix="kg/day"
                    valueStyle={{ fontSize: 18 }} />
                </Col>
              </Row>
            </Card>

            <Card size="small" title="Machines">
              <Table size="small" pagination={false} rowKey="id"
                dataSource={activeCampaign.machines}
                columns={[
                  { title: 'Machine', dataIndex: 'machine_id', width: 160,
                    render: (v: string) => {
                      const m = machineById[v]
                      const name = m?.machine_name?.trim() || m?.machine_code || v.slice(0, 8) + '…'
                      return (
                        <Space direction="vertical" size={0}>
                          <Text strong style={{ fontSize: 12 }}>{name}</Text>
                          {m?.machine_code && m?.machine_name?.trim() && (
                            <Text type="secondary" style={{ fontSize: 10 }}>{m.machine_code}</Text>
                          )}
                        </Space>
                      )
                    } },
                  { title: 'Spindles', dataIndex: 'spindle_count', width: 80, align: 'right' },
                  { title: 'GPS', dataIndex: 'computed_gps', width: 80, align: 'right',
                    render: (v: string | null) => v ? Number(v).toFixed(1) : '—' },
                  { title: 'kg/day', dataIndex: 'computed_daily_kg', width: 90, align: 'right',
                    render: (v: string | null) => v ? Number(v).toFixed(0) : '—' },
                  { title: 'Actual', dataIndex: 'actual_kg', width: 90, align: 'right',
                    render: (v: string) => Number(v).toFixed(0) },
                ]}
              />
            </Card>

            {activeCampaign.notes_to_production && (
              <Alert type="info" message="Notes to Production"
                description={activeCampaign.notes_to_production} />
            )}
            {activeCampaign.notes && (
              <Alert type="warning" message="Notes" description={activeCampaign.notes} />
            )}
          </Space>
        )}
      </Drawer>

      {/* PROD-001A.1 — Manage machine assignments on an existing campaign */}
      <ManageMachinesDrawer
        campaign={manageMachinesFor}
        allMachines={machines}
        onClose={() => setManageMachinesFor(null)}
      />
    </div>
  )
}

// ─── Manage Machines drawer ────────────────────────────────────────

interface ManageMachinesDrawerProps {
  campaign: Campaign | null
  allMachines: MachineRow[]
  onClose: () => void
}

interface AssignedMachine {
  id: string
  machine_id: string
  machine_code: string | null
  machine_name: string | null
  machine_type: string | null
  spindle_count: number
  computed_daily_kg: number | null
  actual_kg: number
}

function ManageMachinesDrawer({ campaign, allMachines, onClose }: ManageMachinesDrawerProps) {
  const qc = useQueryClient()
  const { message: msg, modal } = App.useApp()
  const [picker, setPicker] = useState<string[]>([])

  const assignedQ = useQuery<AssignedMachine[]>({
    enabled: !!campaign,
    queryKey: ['campaign-machines', campaign?.id],
    queryFn: async () => {
      const r = await apiClient.get<AssignedMachine[]>(
        `/production-campaigns/${campaign!.id}/machines`,
      )
      return r.data
    },
  })

  const addMut = useMutation({
    mutationFn: (ids: string[]) =>
      apiClient.post(`/production-campaigns/${campaign!.id}/machines`, {
        machine_ids: ids,
      }),
    onSuccess: (r) => {
      const n = (r.data as { added?: number })?.added ?? 0
      msg.success(n > 0 ? `${n} machine(s) added` : 'No new machines (already assigned)')
      setPicker([])
      qc.invalidateQueries({ queryKey: ['campaign-machines', campaign?.id] })
      qc.invalidateQueries({ queryKey: ['production-campaigns'] })
    },
    onError: (e: any) => msg.error(e?.response?.data?.detail ?? 'Add failed'),
  })

  const removeMut = useMutation({
    mutationFn: (machine_id: string) =>
      apiClient.delete(`/production-campaigns/${campaign!.id}/machines/${machine_id}`),
    onSuccess: () => {
      msg.success('Removed')
      qc.invalidateQueries({ queryKey: ['campaign-machines', campaign?.id] })
      qc.invalidateQueries({ queryKey: ['production-campaigns'] })
    },
    onError: (e: any) => msg.error(e?.response?.data?.detail ?? 'Remove failed'),
  })

  const assigned = assignedQ.data ?? []
  const assignedIds = new Set(assigned.map((a) => a.machine_id))
  const pickable = allMachines.filter((m) => !assignedIds.has(m.id) && m.is_active)

  // Group pickable machines by type for the quick-add chips
  const pickableByType: Record<string, MachineRow[]> = {}
  for (const m of pickable) {
    const t = m.machine_type || 'OTHER'
    if (!pickableByType[t]) pickableByType[t] = []
    pickableByType[t].push(m)
  }
  const pickableTypes = Object.keys(pickableByType).sort()

  return (
    <Drawer
      title={campaign ? (
        <Space>
          <Text>Machines for</Text>
          <Text code>{campaign.campaign_no}</Text>
          <Tag color={STATUS_TAG[campaign.status]?.color}>
            {STATUS_TAG[campaign.status]?.label}
          </Tag>
        </Space>
      ) : ''}
      open={!!campaign}
      onClose={onClose}
      width={720}
      destroyOnClose
    >
      {campaign && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message={`${assigned.length} machine(s) currently assigned · ${pickable.length} available to add`}
            description="Adding machines makes their Daily Shift Log drafts auto-tag MAKE_TO_ORDER for this SO. Removing is blocked if production has already been recorded under the machine for this campaign."
          />

          {/* Add picker — PROMINENT */}
          <Card
            size="small"
            title={
              <Space>
                <PlusOutlined style={{ color: '#1677ff' }} />
                <Text strong style={{ fontSize: 14 }}>
                  Add Machines ({pickable.length} available)
                </Text>
              </Space>
            }
            style={{ borderColor: '#1677ff' }}
          >
            <Space style={{ width: '100%' }} direction="vertical" size={8}>
              {/* Quick-add chips per machine type */}
              {pickableTypes.length > 0 && (
                <div>
                  <Text type="secondary" style={{ fontSize: 11, marginRight: 8 }}>
                    Quick-add by type:
                  </Text>
                  {pickableTypes.map((t) => (
                    <Tag.CheckableTag
                      key={t}
                      checked={pickableByType[t].every((m) => picker.includes(m.id))}
                      onChange={(checked) => {
                        const ids = pickableByType[t].map((m) => m.id)
                        if (checked) {
                          setPicker([...new Set([...picker, ...ids])])
                        } else {
                          setPicker(picker.filter((id) => !ids.includes(id)))
                        }
                      }}
                    >
                      {t} ({pickableByType[t].length})
                    </Tag.CheckableTag>
                  ))}
                </div>
              )}

              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder={
                  pickable.length === 0
                    ? 'All active machines are already assigned'
                    : 'Or pick individually — type to search'
                }
                value={picker}
                onChange={setPicker}
                showSearch
                maxTagCount="responsive"
                filterOption={(input, option) =>
                  String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={pickable.map((m) => ({
                  value: m.id,
                  label: `${m.machine_code} — ${m.machine_name} (${m.machine_type})`,
                }))}
                disabled={pickable.length === 0}
              />

              <Space>
                <Button
                  type="primary"
                  size="large"
                  icon={<PlusOutlined />}
                  disabled={picker.length === 0}
                  loading={addMut.isPending}
                  onClick={() => addMut.mutate(picker)}
                >
                  Add {picker.length > 0 ? `${picker.length} Machine${picker.length === 1 ? '' : 's'}` : 'Machines'}
                </Button>
                {picker.length > 0 && (
                  <Button onClick={() => setPicker([])}>Clear</Button>
                )}
                {pickable.length > 0 && picker.length === 0 && (
                  <Button onClick={() => setPicker(pickable.map((m) => m.id))}>
                    Select all {pickable.length}
                  </Button>
                )}
              </Space>
            </Space>
          </Card>

          {/* Assigned list */}
          <Card size="small" title={`Assigned (${assigned.length})`}>
            {assignedQ.isLoading && <Text type="secondary">Loading…</Text>}
            {!assignedQ.isLoading && assigned.length === 0 && (
              <Empty description="No machines assigned yet" />
            )}
            {!assignedQ.isLoading && assigned.length > 0 && (
              <Table<AssignedMachine>
                size="small"
                rowKey="id"
                dataSource={assigned}
                pagination={false}
                columns={[
                  {
                    title: 'Machine',
                    key: 'machine',
                    render: (_, r) => (
                      <Space size={4}>
                        <Text strong>{r.machine_code}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>{r.machine_name}</Text>
                      </Space>
                    ),
                  },
                  {
                    title: 'Type',
                    dataIndex: 'machine_type',
                    key: 'machine_type',
                    width: 110,
                    render: (v: string | null) => v ? <Tag>{v}</Tag> : null,
                  },
                  {
                    title: 'Spindles',
                    dataIndex: 'spindle_count',
                    key: 'spindle_count',
                    width: 80,
                    align: 'right',
                  },
                  {
                    title: 'Daily kg',
                    dataIndex: 'computed_daily_kg',
                    key: 'computed_daily_kg',
                    width: 90,
                    align: 'right',
                    render: (v: number | null) => v != null ? v.toFixed(1) : '—',
                  },
                  {
                    title: 'Actual kg',
                    dataIndex: 'actual_kg',
                    key: 'actual_kg',
                    width: 90,
                    align: 'right',
                    render: (v: number) => v > 0 ? <Text strong>{v.toFixed(1)}</Text> : '—',
                  },
                  {
                    title: '',
                    key: 'remove',
                    width: 50,
                    render: (_, r) => (
                      <Button
                        size="small"
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        disabled={r.actual_kg > 0}
                        title={r.actual_kg > 0 ? 'Cannot remove — has production' : 'Remove'}
                        onClick={() => modal.confirm({
                          title: `Remove ${r.machine_code} from this campaign?`,
                          content: 'The machine’s future Daily Shift Log drafts will no longer auto-link to this SO. Past logs are unaffected.',
                          onOk: () => removeMut.mutate(r.machine_id),
                        })}
                      />
                    ),
                  },
                ]}
              />
            )}
          </Card>
        </Space>
      )}
    </Drawer>
  )
}
